/* eslint-disable indent */
import { useEffect, useState } from "react";
import { SortableContainer, SortableElement } from "react-sortable-hoc";
import {
  useListContext,
  useDataProvider,
  Loading,
  Error,
  useRedirect,
  useRecordContext,
  useRefresh,
} from "react-admin";
import SaveIcon from "@mui/icons-material/Save";
import { arrayMoveImmutable } from "array-move";
import isEqual from "lodash/isEqual";

import resources from "../../constants/resources";
import {
  CONTENT_ITEM_TYPE_RENDERER,
  ALL_CONTENT_ITEM_TYPE_LIST,
  HEADER_ID,
} from "../../constants/contentConstants";
import {
  ListItemField,
  HeaderField,
  PreviewHeaderField,
  IdField,
  ContentItemTypeField,
  PreviewContentItemField,
} from "../../styles/orderableList";
import {
  OrderableListStyles,
  AddAndOrderButtonStyles,
} from "../../styles/commonStyles";
import { SAVE_CONTENT_ITEM_ORDER_LABEL } from "../../constants/titleConstants";
import DragHandle from "../common/DragHandle";

const HeaderItem = () => {
  const headerData = {
    [HEADER_ID]: {
      id: "Id",
      preview: "Preview",
      content_item_type: "Content Item Type",
    },
  };
  return (
    <ListItemField>
      <HeaderField record={headerData[HEADER_ID]} source="id" flexbasis="5" />
      <HeaderField
        record={headerData[HEADER_ID]}
        source="content_item_type"
        flexbasis="16"
      />
      <PreviewHeaderField record={headerData[HEADER_ID]} source="preview" />
    </ListItemField>
  );
};

const SortableItem = SortableElement(({ currentId, data }) =>
  data[currentId] ? (
    <ListItemField>
      <IdField record={data[currentId]} source="id" />
      <ContentItemTypeField
        record={data[currentId]}
        source="content_item_type"
        choices={ALL_CONTENT_ITEM_TYPE_LIST}
      />
      <PreviewContentItemField
        record={data[currentId]}
        label="Preview"
        render={record =>
          CONTENT_ITEM_TYPE_RENDERER[record.content_item_type]
            ? CONTENT_ITEM_TYPE_RENDERER[record.content_item_type].previewField(
                { record }
              )
            : null
        }
      />
      <DragHandle />
    </ListItemField>
  ) : null
);

const SortableListContainer = SortableContainer(({ ...props }) => {
  const record = useRecordContext();
  return (
    <OrderableListStyles
      resource={resources.CONTENT_ITEMS.url}
      filter={{ content: record.id }}
      perPage={200}
      pagination={false}
      actions={false}
      sort={{ field: "order", order: "ASC" }}
    >
      <SortableGrid setItems={props.setItems} items={props.items} {...props} />
    </OrderableListStyles>
  );
});

const SortableGrid = ({ ...props }) => {
  const { data = [] } = useListContext();
  const { items, setItems } = props;
  const contentItemsData = data.reduce((accumulator, item) => {
    accumulator[item.id] = item;
    return accumulator;
  }, {});
  useEffect(() => {
    const ids = data.map(item => item.id);
    if (!isEqual([...[HEADER_ID].concat(ids)].sort(), [...items].sort())) {
      const orderSortedIds = Object.values(data)
        .sort((a, b) => a.order - b.order)
        .map(contentItem => contentItem.id);
      setItems([HEADER_ID].concat(orderSortedIds));
    }
  }, [data]);
  return (
    <div style={{ margin: "1em" }}>
      {items.map((id, index) => {
        if (id === HEADER_ID) {
          return <HeaderItem />;
        }
        return (
          <SortableItem
            key={id}
            currentId={id}
            data={contentItemsData}
            index={index}
            value={id}
            {...props}
          />
        );
      })}
    </div>
  );
};

const SortableList = ({ ...props }) => {
  const [items, setItems] = useState([]);
  const [hasOrderChanged, setHasOrderChanged] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();
  const dataProvider = useDataProvider();
  const redirect = useRedirect();
  const record = useRecordContext();
  const refresh = useRefresh();

  const onSortEnd = ({ oldIndex, newIndex }) => {
    setItems(prevItems => arrayMoveImmutable(prevItems, oldIndex, newIndex));
    setHasOrderChanged(true);
  };

  const handleSaveContentItemOrder = () => {
    setLoading(true);
    const contentItems = items.length > 0 ? items.slice(1) : items;
    const contentItemObjs = contentItems.map(contentItem => ({
      id: contentItem,
    }));
    dataProvider
      .create(resources.CONTENT_ITEMS_ORDERABLE.url, {
        data: contentItemObjs,
      })
      .then(() => {
        setLoading(false);
        setHasOrderChanged(false);
        redirect(`/${resources.CONTENT.url}/${record.id}/content_items`);
        refresh();
      })
      .catch(err => {
        setError(err);
        setLoading(false);
      });
  };

  return (
    <>
      <SortableListContainer
        {...props}
        onSortEnd={onSortEnd}
        useDragHandle
        lockAxis="y"
        items={items}
        setItems={setItems}
      />
      {error && <Error error={error} />}
      {loading ? (
        <Loading />
      ) : (
        <AddAndOrderButtonStyles
          variant="contained"
          onClick={handleSaveContentItemOrder}
          title={SAVE_CONTENT_ITEM_ORDER_LABEL}
          disabled={!hasOrderChanged}
          startIcon={<SaveIcon />}
        >
          {SAVE_CONTENT_ITEM_ORDER_LABEL}
        </AddAndOrderButtonStyles>
      )}
    </>
  );
};

const ContentItemOrderableList = props => <SortableList {...props} />;

export default ContentItemOrderableList;
